// client/src/components/configuracoes/disponibilidade/FiltroDisponibilidade.js - VERSÃO COMPLETA CORRIGIDA
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Search, Briefcase, User, Box, Building, Globe, Clock, Calendar, Wrench } from 'lucide-react';
import PropTypes from 'prop-types';
import { add, isBefore, isAfter, startOfDay, format, getDay, isSameDay, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { useDiasDeTrabalho } from '../../../hooks/useHorarios';

const extractId = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (Array.isArray(item)) return item.length > 0 ? extractId(item[0]) : '';
    if (typeof item === 'object') {
        if (item.toString && typeof item.toString === 'function' && item.toString().match(/^[0-9a-fA-F]{24}$/)) {
            return item.toString();
        }
        if (item.$oid) return String(item.$oid);
        if (item._id) {
            if (typeof item._id === 'object' && item._id.$oid) return String(item._id.$oid);
            return String(item._id);
        }
        if (item.id) return String(item.id);
    }
    return String(item);
};

const getArrayData = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.data && Array.isArray(data.data)) return data.data;
    if (data.data && data.data.data && Array.isArray(data.data.data)) return data.data.data;
    return [];
};

// ✅ CSS para o calendário com z-index corrigido
const calendarStyles = `
    .rdp-day_available {
        background-color: #dcfce7 !important;
        color: #166534 !important;
        font-weight: bold !important;
        border-radius: 100% !important;
    }
    .rdp-day_available:hover {
        background-color: #bbf7d0 !important;
        transform: scale(1.05);
    }
    .rdp-day_selected:not(.rdp-day_disabled) {
        background-color: #3b82f6 !important;
        color: white !important;
        font-weight: bold !important;
    }
    .rdp-day_disabled {
        opacity: 0.4 !important;
        text-decoration: line-through !important;
        cursor: not-allowed !important;
    }
    
    /* ✅ CORREÇÃO DE SOBREPOSIÇÃO - z-index hierarchy */
    .calendar-portal {
        position: relative;
        z-index: 99999 !important;
    }
    
    .calendar-dropdown {
        position: absolute;
        z-index: 100000 !important;
        background: white;
        border-radius: 0.5rem;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.02);
    }
    
    /* Garantir que o calendário fique acima de tudo */
    .rdp {
        z-index: 100001 !important;
    }
    
    /* Overlay para bloquear interação com elementos atrás */
    .calendar-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 99990;
        background: transparent;
    }
`;

// ✅ Função para normalizar data UTC para data local
const normalizeToLocalDate = (dateString) => {
    if (!dateString) return null;
    
    try {
        if (typeof dateString === 'string') {
            const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                const [, year, month, day] = match;
                return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
            }
        }
        
        if (dateString instanceof Date && !isNaN(dateString.getTime())) {
            return new Date(
                dateString.getUTCFullYear(),
                dateString.getUTCMonth(),
                dateString.getUTCDate(),
                12, 0, 0
            );
        }
        
        const parsed = parseISO(dateString);
        if (!isNaN(parsed.getTime())) {
            return new Date(
                parsed.getUTCFullYear(),
                parsed.getUTCMonth(),
                parsed.getUTCDate(),
                12, 0, 0
            );
        }
    } catch (e) {
        console.error('Erro ao normalizar data:', e);
    }
    
    return null;
};

const FiltroDisponibilidade = ({ 
    filtros, setFiltros, 
    tipoBusca, setTipoBusca, 
    onSubmit, loading,
    servicos = [], 
    recursosComAgenda = [], 
    profissionais = [],
    unidadesDaConta = [],
    userServicos = [],
    userUnidades = [] 
}) => {
    
    const [isCalendarOpenInicio, setIsCalendarOpenInicio] = useState(false);
    const [isCalendarOpenFim, setIsCalendarOpenFim] = useState(false);
    
    // ✅ Refs para os containers dos calendários
    const calendarInicioRef = useRef(null);
    const calendarFimRef = useRef(null);
    const buttonInicioRef = useRef(null);
    const buttonFimRef = useRef(null);
    
    const [entidadeCalendarioId, setEntidadeCalendarioId] = useState(null);
    const [tipoEntidadeCalendario, setTipoEntidadeCalendario] = useState('User');
    
    const onChange = e => setFiltros({ ...filtros, [e.target.name]: e.target.value });
    
    const onTipoBuscaChange = (novoTipo) => {
        setTipoBusca(novoTipo);
        setFiltros(prev => ({
            ...prev,
            servicoId: '',
            profissionalId: '',
            recursoId: '',
            duracaoManual: '',
            duracaoUnidade: 'minutos'
        }));
    };

    const handleUnidadeChange = (e) => {
        const val = e.target.value;
        setFiltros(prev => ({
            ...prev,
            unidadeId: val,
            ...(tipoBusca === 'servico' || tipoBusca === 'servicoExterno' ? { profissionalId: '', servicoId: '' } : {})
        }));
    };

    const handleProfissionalChange = (e) => {
        const val = e.target.value;
        setFiltros(prev => ({
            ...prev,
            profissionalId: val,
            ...(tipoBusca === 'servico' || tipoBusca === 'servicoExterno' ? { servicoId: '' } : {})
        }));
        
        if (val) {
            setEntidadeCalendarioId(val);
            setTipoEntidadeCalendario('User');
        }
    };

    const handleServicoChange = (e) => {
        const val = e.target.value;
        const servicoSelecionado = getArrayData(servicos).find(s => extractId(s) === val);
        
        if (servicoSelecionado && (tipoBusca === 'servico' || tipoBusca === 'servicoExterno')) {
            const duracaoEmMinutos = servicoSelecionado.duracaoSessaoEmMinutos || 60;
            let duracaoManual = duracaoEmMinutos;
            let duracaoUnidade = 'minutos';
            
            if (duracaoEmMinutos >= 60 && duracaoEmMinutos % 60 === 0) {
                duracaoManual = duracaoEmMinutos / 60;
                duracaoUnidade = 'horas';
            }
            
            setFiltros(prev => ({
                ...prev,
                servicoId: val,
                duracaoManual: duracaoManual.toString(),
                duracaoUnidade: duracaoUnidade
            }));
        } else {
            setFiltros(prev => ({
                ...prev,
                servicoId: val
            }));
        }
    };

    const handleRecursoChange = (e) => {
        const val = e.target.value;
        const recursoSelecionado = getArrayData(recursosComAgenda).find(r => extractId(r) === val);
        
        if (recursoSelecionado && recursoSelecionado.unidade) {
            const unidadeId = extractId(recursoSelecionado.unidade);
            setFiltros(prev => ({
                ...prev,
                recursoId: val,
                unidadeId: unidadeId
            }));
        } else {
            setFiltros(prev => ({
                ...prev,
                recursoId: val
            }));
        }
        
        if (val) {
            setEntidadeCalendarioId(val);
            setTipoEntidadeCalendario('Recurso');
        }
    };

    const safeProfissionais = getArrayData(profissionais);
    const safeServicos = getArrayData(servicos);
    const safeUserServicos = getArrayData(userServicos);
    const safeUserUnidades = getArrayData(userUnidades);
    const safeRecursos = getArrayData(recursosComAgenda);

    const profissionaisDaUnidade = useMemo(() => {
        if (!filtros.unidadeId) return [];
        if (tipoBusca === 'servicoExterno') return [];
        
        const profIdsValidos = new Set();
        
        if (safeUserUnidades.length > 0) {
            safeUserUnidades.forEach(uu => {
                if (uu.status === 'ativo') {
                    const uIdStr = extractId(uu.unidade);
                    if (uIdStr === filtros.unidadeId && uu.user) {
                        profIdsValidos.add(extractId(uu.user));
                    }
                }
            });
            
            return safeProfissionais.filter(p => profIdsValidos.has(extractId(p)));
        }
        
        return [];
    }, [filtros.unidadeId, safeUserUnidades, safeProfissionais, tipoBusca]);

    const profissionaisExternos = useMemo(() => {
        if (tipoBusca !== 'servicoExterno') return [];
        return safeProfissionais.filter(p => p._id);
    }, [safeProfissionais, tipoBusca]);

    const servicosDisponiveis = useMemo(() => {
        if (!filtros.unidadeId && tipoBusca !== 'servicoExterno') return [];
        
        if (tipoBusca === 'servicoExterno') {
            return safeServicos.filter(s => s.ativo !== false);
        }
        
        const servicosIdsValidos = new Set();
        let possuiRelacionamentoRegistrado = false;

        const profsValidadosNaUnidade = new Set(profissionaisDaUnidade.map(p => extractId(p)));

        if (safeUserServicos.length > 0) {
            safeUserServicos.forEach(us => {
                if (us.status !== 'ativo') return;
                
                let unidadesDesteRelacionamento = [];
                if (us.unidades && Array.isArray(us.unidades)) {
                    unidadesDesteRelacionamento = us.unidades.map(u => extractId(u));
                } else if (us.unidade) {
                    unidadesDesteRelacionamento = [extractId(us.unidade)];
                }

                const atendeNestaUnidade = unidadesDesteRelacionamento.includes(filtros.unidadeId);
                
                if (atendeNestaUnidade && us.servico && us.user) {
                    possuiRelacionamentoRegistrado = true;
                    const userIdStr = extractId(us.user);
                    
                    if (profsValidadosNaUnidade.has(userIdStr) || profissionaisDaUnidade.length === 0) {
                        if (filtros.profissionalId) {
                            if (userIdStr === filtros.profissionalId) {
                                servicosIdsValidos.add(extractId(us.servico));
                            }
                        } else {
                            servicosIdsValidos.add(extractId(us.servico));
                        }
                    }
                }
            });
        }

        if (possuiRelacionamentoRegistrado && servicosIdsValidos.size > 0) {
            return safeServicos.filter(s => servicosIdsValidos.has(extractId(s)) && s.ativo !== false);
        }
        
        let listaFallback = safeServicos.filter(s => {
            if (s.ativo === false) return false;
            if (!s.unidadesDetalhes || s.unidadesDetalhes.length === 0) return false;
            
            return s.unidadesDetalhes.some(ud => {
                const uidStr = extractId(ud.unidade || ud);
                return uidStr === filtros.unidadeId && ud.ativo !== false;
            });
        });

        if (filtros.profissionalId) {
            listaFallback = listaFallback.filter(s => {
                if (!s.profissionaisHabilitados || s.profissionaisHabilitados.length === 0) return false;
                return s.profissionaisHabilitados.some(p => extractId(p) === filtros.profissionalId);
            });
        }
        
        return listaFallback;

    }, [filtros.unidadeId, filtros.profissionalId, safeUserServicos, profissionaisDaUnidade, safeServicos, tipoBusca]);

    const recursosExternos = useMemo(() => {
        if (tipoBusca !== 'recursoExterno') return [];
        return safeRecursos.filter(r => r.disponivelPara?.includes('EXTERNO_LOCACAO'));
    }, [safeRecursos, tipoBusca]);

    const duracaoEmMinutos = useMemo(() => {
        if (filtros.duracaoUnidade === 'horas') {
            return (parseInt(filtros.duracaoManual) || 0) * 60;
        }
        return parseInt(filtros.duracaoManual) || 0;
    }, [filtros.duracaoManual, filtros.duracaoUnidade]);

    // ✅ Hook para buscar dias de trabalho (corrigido com normalização)
    const { data: diasDeTrabalhoData } = useDiasDeTrabalho(entidadeCalendarioId, tipoEntidadeCalendario);
    
    const diasSemanaTrabalho = useMemo(() => diasDeTrabalhoData?.diasSemana || [], [diasDeTrabalhoData]);
    
    // ✅ Normaliza as datas únicas para comparação correta
    const datasUnicasTrabalho = useMemo(() => {
        const rawDatas = diasDeTrabalhoData?.datasUnicas || [];
        return rawDatas.map(d => normalizeToLocalDate(d)).filter(Boolean);
    }, [diasDeTrabalhoData]);
    
    // ✅ Função corrigida para verificar dia de trabalho (ignora timezone)
    const isDiaDeTrabalho = useCallback((date) => {
        if (!entidadeCalendarioId) return true;
        if (!date) return false;
        
        // Normaliza a data para comparação
        const dateNormalized = normalizeToLocalDate(date);
        if (!dateNormalized) return false;
        
        // Verifica se é uma data única (comparação normalizada)
        const isDataUnica = datasUnicasTrabalho.some(dataUnica => {
            if (!dataUnica) return false;
            return dataUnica.getFullYear() === dateNormalized.getFullYear() &&
                   dataUnica.getMonth() === dateNormalized.getMonth() &&
                   dataUnica.getDate() === dateNormalized.getDate();
        });
        
        if (isDataUnica) return true;
        
        // Verifica dias da semana
        if (diasSemanaTrabalho.length > 0) {
            const dayOfWeek = getDay(date);
            return diasSemanaTrabalho.includes(dayOfWeek);
        }
        
        // Se não tem regras configuradas, considera todos os dias disponíveis
        return false;
    }, [datasUnicasTrabalho, diasSemanaTrabalho, entidadeCalendarioId]);
    
    const isDateAvailable = useCallback((day) => {
        if (isBefore(startOfDay(day), startOfDay(new Date()))) return false;
        if (entidadeCalendarioId && !isDiaDeTrabalho(day)) return false;
        const dataMaxima = add(startOfDay(new Date()), { days: 30 });
        if (isAfter(day, dataMaxima)) return false;
        return true;
    }, [isDiaDeTrabalho, entidadeCalendarioId]);

    const selectedDateInicio = useMemo(() => {
        if (!filtros.dataInicio) return new Date();
        return parseISO(filtros.dataInicio);
    }, [filtros.dataInicio]);
    
    const selectedDateFim = useMemo(() => {
        if (!filtros.dataFim) return new Date();
        return parseISO(filtros.dataFim);
    }, [filtros.dataFim]);

    const handleDateSelectInicio = (date) => {
        if (!date) return;
        const dataFormatada = format(date, 'yyyy-MM-dd');
        setFiltros(prev => ({ ...prev, dataInicio: dataFormatada }));
        setIsCalendarOpenInicio(false);
        
        // Se a data fim for anterior à data início, atualiza automaticamente
        if (selectedDateFim && isBefore(selectedDateFim, date)) {
            setFiltros(prev => ({ ...prev, dataFim: dataFormatada }));
        }
    };

    const handleDateSelectFim = (date) => {
        if (!date) return;
        const dataFormatada = format(date, 'yyyy-MM-dd');
        setFiltros(prev => ({ ...prev, dataFim: dataFormatada }));
        setIsCalendarOpenFim(false);
    };

    useEffect(() => {
        if (tipoBusca === 'profissional' && filtros.profissionalId) {
            setEntidadeCalendarioId(filtros.profissionalId);
            setTipoEntidadeCalendario('User');
        } else if (tipoBusca === 'recurso' && filtros.recursoId) {
            setEntidadeCalendarioId(filtros.recursoId);
            setTipoEntidadeCalendario('Recurso');
        } else if (tipoBusca === 'recursoExterno' && filtros.recursoId) {
            setEntidadeCalendarioId(filtros.recursoId);
            setTipoEntidadeCalendario('Recurso');
        } else if (tipoBusca === 'unidade' && filtros.unidadeId) {
            setEntidadeCalendarioId(filtros.unidadeId);
            setTipoEntidadeCalendario('Unidade');
        }
    }, [tipoBusca, filtros.profissionalId, filtros.recursoId, filtros.unidadeId]);

    // ✅ CORREÇÃO DE SOBREPOSIÇÃO - Fechar calendários ao clicar fora com refs
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Fechar calendário de início
            if (isCalendarOpenInicio && 
                calendarInicioRef.current && 
                !calendarInicioRef.current.contains(event.target) &&
                buttonInicioRef.current && 
                !buttonInicioRef.current.contains(event.target)) {
                setIsCalendarOpenInicio(false);
            }
            
            // Fechar calendário de fim
            if (isCalendarOpenFim && 
                calendarFimRef.current && 
                !calendarFimRef.current.contains(event.target) &&
                buttonFimRef.current && 
                !buttonFimRef.current.contains(event.target)) {
                setIsCalendarOpenFim(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isCalendarOpenInicio, isCalendarOpenFim]);

    // ✅ Prevenir scroll da página quando calendário estiver aberto
    useEffect(() => {
        if (isCalendarOpenInicio || isCalendarOpenFim) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        
        return () => {
            document.body.style.overflow = '';
        };
    }, [isCalendarOpenInicio, isCalendarOpenFim]);

    return (
        <div className="content-panel" style={{ position: 'relative', zIndex: 10 }}>
            <style>{calendarStyles}</style>
            <h3 className="panel-title">Filtros de Consulta</h3>
            <form onSubmit={onSubmit} noValidate>
                <fieldset className="form-section">
                    <legend className="form-section-title">1. O que você deseja consultar?</legend>
                    <div className="service-category-filters">
                        <button type="button" className={`category-button ${tipoBusca === 'servico' ? 'is-active' : ''}`} onClick={() => onTipoBuscaChange('servico')}>
                            <Briefcase size={16} className="icon" /> Serviço Interno
                        </button>
                        <button type="button" className={`category-button ${tipoBusca === 'servicoExterno' ? 'is-active' : ''}`} onClick={() => onTipoBuscaChange('servicoExterno')}>
                            <Globe size={16} className="icon" /> Serviço Externo
                        </button>
                        <button type="button" className={`category-button ${tipoBusca === 'profissional' ? 'is-active' : ''}`} onClick={() => onTipoBuscaChange('profissional')}>
                            <User size={16} className="icon" /> Apenas um Profissional
                        </button>
                        <button type="button" className={`category-button ${tipoBusca === 'recurso' ? 'is-active' : ''}`} onClick={() => onTipoBuscaChange('recurso')}>
                            <Box size={16} className="icon" /> Locação Interna
                        </button>
                        <button type="button" className={`category-button ${tipoBusca === 'recursoExterno' ? 'is-active' : ''}`} onClick={() => onTipoBuscaChange('recursoExterno')}>
                            <Globe size={16} className="icon" /> Locação Externa
                        </button>
                        <button type="button" className={`category-button ${tipoBusca === 'unidade' ? 'is-active' : ''}`} onClick={() => onTipoBuscaChange('unidade')}>
                            <Building size={16} className="icon" /> Apenas a Unidade
                        </button>
                    </div>
                </fieldset>

                <fieldset className="form-section">
                    <legend className="form-section-title">2. Especifique os detalhes</legend>
                    <div className="form-grid-container">
                        
                        <div className="form-group full-width">
                            <label htmlFor="unidadeId">
                                Unidade de Atendimento*
                                {(tipoBusca === 'servicoExterno' || tipoBusca === 'recursoExterno') && 
                                    <span className="text-xs text-gray-500 ml-2">(Referência para disponibilidade)</span>
                                }
                            </label>
                            <select 
                                id="unidadeId" 
                                name="unidadeId" 
                                value={filtros.unidadeId || ''} 
                                onChange={handleUnidadeChange} 
                                className="form-input" 
                                required 
                                disabled={loading}
                            >
                                <option value="">-- Selecione uma Unidade --</option>
                                {getArrayData(unidadesDaConta).map(unidade => {
                                    const idSeguro = extractId(unidade);
                                    return <option key={idSeguro} value={idSeguro}>{unidade.nome}</option>
                                })}
                            </select>
                            <p className="form-hint">
                                {tipoBusca === 'servicoExterno' ? 
                                    "Unidade de referência para o atendimento externo" : 
                                    tipoBusca === 'recursoExterno' ?
                                    "Unidade onde o recurso externo será utilizado" :
                                    "Selecione a unidade para consulta"}
                            </p>
                        </div>

                        {/* SERVIÇO INTERNO */}
                        {tipoBusca === 'servico' && (
                            <>
                                <div className="form-group">
                                    <label htmlFor="profissionalId">
                                        Profissional (Opcional)
                                        <span className="text-xs text-gray-400 ml-1">- Filtro opcional</span>
                                    </label>
                                    <select 
                                        id="profissionalId" 
                                        name="profissionalId" 
                                        value={filtros.profissionalId} 
                                        onChange={handleProfissionalChange} 
                                        className="form-input"
                                        disabled={loading || !filtros.unidadeId}
                                    >
                                        <option value="">-- Todos os profissionais da unidade --</option>
                                        {profissionaisDaUnidade.map(p => {
                                            const idSeguro = extractId(p);
                                            return <option key={idSeguro} value={idSeguro}>{p.nome || p.name}</option>
                                        })}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="servicoId">Serviço*</label>
                                    <select 
                                        id="servicoId" 
                                        name="servicoId" 
                                        value={filtros.servicoId} 
                                        onChange={handleServicoChange} 
                                        className="form-input" 
                                        required
                                        disabled={loading || !filtros.unidadeId}
                                    >
                                        <option value="">-- Selecione um Serviço --</option>
                                        {servicosDisponiveis.map(s => {
                                            const idSeguro = extractId(s);
                                            const duracao = s.duracaoSessaoEmMinutos;
                                            const duracaoTexto = duracao ? ` (${duracao} min)` : '';
                                            return <option key={idSeguro} value={idSeguro}>{s.nome}{duracaoTexto}</option>
                                        })}
                                    </select>
                                    <p className="form-hint">A duração será usada automaticamente a partir do serviço</p>
                                </div>
                            </>
                        )}
                        
                        {/* SERVIÇO EXTERNO */}
                        {tipoBusca === 'servicoExterno' && (
                            <>
                                <div className="form-group">
                                    <label htmlFor="profissionalId">Prestador Externo*</label>
                                    <select 
                                        id="profissionalId" 
                                        name="profissionalId" 
                                        value={filtros.profissionalId} 
                                        onChange={handleProfissionalChange} 
                                        className="form-input" 
                                        required
                                        disabled={loading}
                                    >
                                        <option value="">-- Selecione um Prestador Externo --</option>
                                        {profissionaisExternos.map(p => {
                                            const idSeguro = extractId(p);
                                            return <option key={idSeguro} value={idSeguro}>{p.nome || p.name}</option>
                                        })}
                                    </select>
                                    <p className="form-hint">Prestador externo que realizará o serviço</p>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="servicoId">Serviço*</label>
                                    <select 
                                        id="servicoId" 
                                        name="servicoId" 
                                        value={filtros.servicoId} 
                                        onChange={handleServicoChange} 
                                        className="form-input" 
                                        required
                                        disabled={loading}
                                    >
                                        <option value="">-- Selecione um Serviço --</option>
                                        {servicosDisponiveis.map(s => {
                                            const idSeguro = extractId(s);
                                            const duracao = s.duracaoSessaoEmMinutos;
                                            const duracaoTexto = duracao ? ` (${duracao} min)` : '';
                                            return <option key={idSeguro} value={idSeguro}>{s.nome}{duracaoTexto}</option>
                                        })}
                                    </select>
                                    <p className="form-hint">Serviço a ser realizado pelo prestador externo</p>
                                </div>
                            </>
                        )}
                        
                        {/* PROFISSIONAL (Busca livre) */}
                        {tipoBusca === 'profissional' && (
                            <>
                                <div className="form-group full-width">
                                    <label htmlFor="profissionalId">Profissional*</label>
                                    <select 
                                        id="profissionalId" 
                                        name="profissionalId" 
                                        value={filtros.profissionalId} 
                                        onChange={onChange} 
                                        className="form-input" 
                                        required
                                        disabled={loading}
                                    >
                                        <option value="">-- Selecione um Profissional --</option>
                                        {safeProfissionais.map(p => {
                                            const idSeguro = extractId(p);
                                            return <option key={idSeguro} value={idSeguro}>{p.nome || p.name}</option>
                                        })}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="duracaoManual">Duração da Consulta*</label>
                                    <div className="flex items-center gap-2">
                                        <input 
                                            type="number" 
                                            id="duracaoManual" 
                                            name="duracaoManual" 
                                            value={filtros.duracaoManual} 
                                            onChange={onChange} 
                                            className="form-input w-2/3" 
                                            required 
                                            disabled={loading}
                                            min="1"
                                            placeholder="Ex: 30"
                                        />
                                        <select 
                                            id="duracaoUnidade" 
                                            name="duracaoUnidade" 
                                            value={filtros.duracaoUnidade} 
                                            onChange={onChange} 
                                            className="form-input w-1/3"
                                            disabled={loading}
                                        >
                                            <option value="minutos">Minutos</option>
                                            <option value="horas">Horas</option>
                                        </select>
                                    </div>
                                    <p className="form-hint">Duração total da consulta com o profissional</p>
                                </div>
                            </>
                        )}
                        
                        {/* LOCAÇÃO INTERNA */}
                        {tipoBusca === 'recurso' && (
                            <>
                                <div className="form-group full-width">
                                    <label htmlFor="recursoId">Recurso para Locação*</label>
                                    <select 
                                        id="recursoId" 
                                        name="recursoId" 
                                        value={filtros.recursoId} 
                                        onChange={handleRecursoChange} 
                                        className="form-input" 
                                        required
                                        disabled={loading}
                                    >
                                        <option value="">-- Selecione um Recurso --</option>
                                        {safeRecursos.map(r => {
                                            const idSeguro = extractId(r);
                                            const tipoIcon = r.tipo === 'Sala' ? '🏢' : r.tipo === 'Equipamento' ? '🔧' : '📦';
                                            return <option key={idSeguro} value={idSeguro}>{tipoIcon} {r.nome} ({r.tipo})</option>
                                        })}
                                    </select>
                                    <p className="form-hint">Recurso que será utilizado internamente</p>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="duracaoManual">Duração da Locação*</label>
                                    <div className="flex items-center gap-2">
                                        <input 
                                            type="number" 
                                            id="duracaoManual" 
                                            name="duracaoManual" 
                                            value={filtros.duracaoManual} 
                                            onChange={onChange} 
                                            className="form-input w-2/3" 
                                            required 
                                            disabled={loading}
                                            min="1"
                                            placeholder="Ex: 2"
                                        />
                                        <select 
                                            id="duracaoUnidade" 
                                            name="duracaoUnidade" 
                                            value={filtros.duracaoUnidade} 
                                            onChange={onChange} 
                                            className="form-input w-1/3"
                                            disabled={loading}
                                        >
                                            <option value="minutos">Minutos</option>
                                            <option value="horas">Horas</option>
                                        </select>
                                    </div>
                                    <p className="form-hint">Tempo de uso do recurso</p>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="finalidadeRecurso">Finalidade da Consulta*</label>
                                    <select 
                                        id="finalidadeRecurso" 
                                        name="finalidadeRecurso" 
                                        value={filtros.finalidadeRecurso || 'INTERNO_LOCACAO'} 
                                        onChange={onChange} 
                                        className="form-input" 
                                        required
                                        disabled={loading}
                                    >
                                        <option value="INTERNO_LOCACAO">Locação Interna</option>
                                    </select>
                                </div>
                            </>
                        )}
                        
                        {/* LOCAÇÃO EXTERNA */}
                        {tipoBusca === 'recursoExterno' && (
                            <>
                                <div className="form-group full-width">
                                    <label htmlFor="recursoId">Recurso Externo*</label>
                                    <select 
                                        id="recursoId" 
                                        name="recursoId" 
                                        value={filtros.recursoId} 
                                        onChange={handleRecursoChange} 
                                        className="form-input" 
                                        required
                                        disabled={loading}
                                    >
                                        <option value="">-- Selecione um Recurso Externo --</option>
                                        {recursosExternos.map(r => {
                                            const idSeguro = extractId(r);
                                            const tipoIcon = r.tipo === 'Equipamento' ? '🔧' : '📦';
                                            return <option key={idSeguro} value={idSeguro}>{tipoIcon} {r.nome} ({r.tipo})</option>
                                        })}
                                    </select>
                                    <p className="form-hint">Recurso externo que será alugado/locado</p>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="duracaoManual">Duração da Locação*</label>
                                    <div className="flex items-center gap-2">
                                        <input 
                                            type="number" 
                                            id="duracaoManual" 
                                            name="duracaoManual" 
                                            value={filtros.duracaoManual} 
                                            onChange={onChange} 
                                            className="form-input w-2/3" 
                                            required 
                                            disabled={loading}
                                            min="1"
                                            placeholder="Ex: 1"
                                        />
                                        <select 
                                            id="duracaoUnidade" 
                                            name="duracaoUnidade" 
                                            value={filtros.duracaoUnidade} 
                                            onChange={onChange} 
                                            className="form-input w-1/3"
                                            disabled={loading}
                                        >
                                            <option value="minutos">Minutos</option>
                                            <option value="horas">Horas</option>
                                        </select>
                                    </div>
                                    <p className="form-hint">Tempo de locação do recurso</p>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="finalidadeRecurso">Finalidade da Consulta*</label>
                                    <select 
                                        id="finalidadeRecurso" 
                                        name="finalidadeRecurso" 
                                        value={filtros.finalidadeRecurso || 'EXTERNO_LOCACAO'} 
                                        onChange={onChange} 
                                        className="form-input" 
                                        required
                                        disabled={loading}
                                    >
                                        <option value="EXTERNO_LOCACAO">Locação Externa</option>
                                    </select>
                                </div>
                            </>
                        )}
                        
                        {/* APENAS UNIDADE */}
                        {tipoBusca === 'unidade' && (
                            <div className="form-group">
                                <label htmlFor="duracaoManual">Duração da Consulta*</label>
                                <div className="flex items-center gap-2">
                                    <input 
                                        type="number" 
                                        id="duracaoManual" 
                                        name="duracaoManual" 
                                        value={filtros.duracaoManual} 
                                        onChange={onChange} 
                                        className="form-input w-2/3" 
                                        required 
                                        disabled={loading}
                                        min="1"
                                        placeholder="Ex: 60"
                                    />
                                    <select 
                                        id="duracaoUnidade" 
                                        name="duracaoUnidade" 
                                        value={filtros.duracaoUnidade} 
                                        onChange={onChange} 
                                        className="form-input w-1/3"
                                        disabled={loading}
                                    >
                                        <option value="minutos">Minutos</option>
                                        <option value="horas">Horas</option>
                                    </select>
                                </div>
                                <p className="form-hint">Duração para verificar disponibilidade na unidade</p>
                            </div>
                        )}

                        {/* ✅ CALENDÁRIO DATA DE INÍCIO - CORRIGIDO */}
                        <div className="form-group relative" style={{ position: 'relative', zIndex: 100 }}>
                            <label htmlFor="dataInicio">
                                <Calendar size={14} className="inline mr-1" /> Data de Início*
                            </label>
                            <div className="relative">
                                <div 
                                    ref={buttonInicioRef}
                                    onClick={() => {
                                        setIsCalendarOpenInicio(!isCalendarOpenInicio);
                                        if (isCalendarOpenFim) setIsCalendarOpenFim(false);
                                    }}
                                    className="flex items-center justify-between p-2 border border-gray-300 rounded-lg cursor-pointer bg-white hover:border-primary"
                                >
                                    <span className="text-gray-700">
                                        {format(selectedDateInicio, 'dd/MM/yyyy')}
                                    </span>
                                    <Calendar size={18} className="text-gray-400" />
                                </div>
                                {isCalendarOpenInicio && (
                                    <>
                                        <div className="calendar-overlay" onClick={() => setIsCalendarOpenInicio(false)} />
                                        <div 
                                            ref={calendarInicioRef}
                                            className="absolute calendar-dropdown"
                                            style={{ 
                                                top: 'calc(100% + 5px)',
                                                left: 0,
                                                zIndex: 100000,
                                                backgroundColor: 'white',
                                                borderRadius: '0.5rem',
                                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.02)'
                                            }}
                                        >
                                            <DayPicker 
                                                key={`inicio-${entidadeCalendarioId}`}
                                                mode="single" 
                                                selected={selectedDateInicio}
                                                onSelect={handleDateSelectInicio} 
                                                locale={ptBR} 
                                                modifiers={{
                                                    available: (day) => isDateAvailable(day)
                                                }}
                                                modifiersClassNames={{
                                                    available: 'rdp-day_available'
                                                }}
                                                disabled={(day) => !isDateAvailable(day)}
                                            />
                                            {entidadeCalendarioId && (
                                                <div className="p-2 text-xs text-gray-500 border-t">
                                                    <p>📅 Dias disponíveis: 
                                                        {diasSemanaTrabalho.length > 0 && (
                                                            <span> {diasSemanaTrabalho.map(d => {
                                                                const diasMap = {0:'Dom',1:'Seg',2:'Ter',3:'Qua',4:'Qui',5:'Sex',6:'Sáb'};
                                                                return diasMap[d];
                                                            }).join(', ')}</span>
                                                        )}
                                                        {datasUnicasTrabalho.length > 0 && (
                                                            <span> + {datasUnicasTrabalho.length} data(s) específica(s)</span>
                                                        )}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                            <p className="form-hint">Selecione a data de início</p>
                        </div>

                        {/* ✅ CALENDÁRIO DATA FINAL - CORRIGIDO */}
                        <div className="form-group relative" style={{ position: 'relative', zIndex: 99 }}>
                            <label htmlFor="dataFim">
                                <Calendar size={14} className="inline mr-1" /> Data Final*
                            </label>
                            <div className="relative">
                                <div 
                                    ref={buttonFimRef}
                                    onClick={() => {
                                        setIsCalendarOpenFim(!isCalendarOpenFim);
                                        if (isCalendarOpenInicio) setIsCalendarOpenInicio(false);
                                    }}
                                    className="flex items-center justify-between p-2 border border-gray-300 rounded-lg cursor-pointer bg-white hover:border-primary"
                                >
                                    <span className="text-gray-700">
                                        {format(selectedDateFim, 'dd/MM/yyyy')}
                                    </span>
                                    <Calendar size={18} className="text-gray-400" />
                                </div>
                                {isCalendarOpenFim && (
                                    <>
                                        <div className="calendar-overlay" onClick={() => setIsCalendarOpenFim(false)} />
                                        <div 
                                            ref={calendarFimRef}
                                            className="absolute calendar-dropdown"
                                            style={{ 
                                                top: 'calc(100% + 5px)',
                                                left: 0,
                                                zIndex: 100000,
                                                backgroundColor: 'white',
                                                borderRadius: '0.5rem',
                                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.02)'
                                            }}
                                        >
                                            <DayPicker 
                                                key={`fim-${entidadeCalendarioId}`}
                                                mode="single" 
                                                selected={selectedDateFim}
                                                onSelect={handleDateSelectFim} 
                                                locale={ptBR} 
                                                modifiers={{
                                                    available: (day) => isDateAvailable(day)
                                                }}
                                                modifiersClassNames={{
                                                    available: 'rdp-day_available'
                                                }}
                                                disabled={(day) => {
                                                    if (isBefore(day, selectedDateInicio)) return true;
                                                    return !isDateAvailable(day);
                                                }}
                                            />
                                            {entidadeCalendarioId && (
                                                <div className="p-2 text-xs text-gray-500 border-t">
                                                    <p>📅 Dias disponíveis: 
                                                        {diasSemanaTrabalho.length > 0 && (
                                                            <span> {diasSemanaTrabalho.map(d => {
                                                                const diasMap = {0:'Dom',1:'Seg',2:'Ter',3:'Qua',4:'Qui',5:'Sex',6:'Sáb'};
                                                                return diasMap[d];
                                                            }).join(', ')}</span>
                                                        )}
                                                        {datasUnicasTrabalho.length > 0 && (
                                                            <span> + {datasUnicasTrabalho.length} data(s) específica(s)</span>
                                                        )}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                            <p className="form-hint">Selecione a data final</p>
                        </div>
                    </div>
                </fieldset>

                {/* Resumo da consulta */}
                {filtros.servicoId && (tipoBusca === 'servico' || tipoBusca === 'servicoExterno') && (
                    <div className="form-section bg-blue-50 p-3 rounded-lg mb-4">
                        <div className="flex items-center gap-2 text-blue-800">
                            <Clock size={16} />
                            <span className="text-sm font-medium">
                                Duração do serviço: {duracaoEmMinutos} minutos
                                {duracaoEmMinutos >= 60 && ` (${Math.floor(duracaoEmMinutos / 60)} hora${Math.floor(duracaoEmMinutos / 60) > 1 ? 's' : ''})`}
                            </span>
                        </div>
                    </div>
                )}

                <div className="form-actions">
                    <button 
                        type="submit" 
                        className="action-button action-button-primary" 
                        disabled={loading}
                    >
                        <Search size={18} className="icon" />
                        {loading ? 'Buscando...' : 'Verificar Disponibilidade'}
                    </button>
                </div>
            </form>
        </div>
    );
};

FiltroDisponibilidade.propTypes = {
    filtros: PropTypes.object.isRequired,
    setFiltros: PropTypes.func.isRequired,
    tipoBusca: PropTypes.string.isRequired,
    setTipoBusca: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    servicos: PropTypes.array,
    recursosComAgenda: PropTypes.array,
    profissionais: PropTypes.array,
    unidadesDaConta: PropTypes.array,
    userServicos: PropTypes.array,
    userUnidades: PropTypes.array
};

export default FiltroDisponibilidade;